import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { ZAR, useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import usePageMeta from "@/lib/useSeo";
import {
  MagnifyingGlass,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Copy,
  ArrowClockwise,
  Repeat,
  CaretDown,
} from "@phosphor-icons/react";

const STATUS_STEPS = [
  { key: "pending", label: "Order placed", Icon: Clock },
  { key: "paid", label: "Payment received", Icon: CheckCircle },
  { key: "shipped", label: "Dispatched", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: Package },
];

const STATUS_INDEX = { pending: 0, initiated: 0, paid: 1, shipped: 2, delivered: 3 };

const POLL_MS = 60_000;

/* --------- Helpers --------- */
function skipWeekend(d) {
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() + 2);
  else if (day === 0) d.setDate(d.getDate() + 1);
  return d;
}
function fmtDay(d) {
  return d.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" });
}
function etaForOrder(order) {
  if (!order) return null;
  const created = new Date(order.created_at);
  const now = new Date();
  if (order.status === "delivered") return "Delivered";
  if (order.status === "shipped") {
    const arrive = skipWeekend(new Date(Math.max(now.getTime() + 24 * 3600 * 1000, created.getTime() + 48 * 3600 * 1000)));
    return `Arriving ${fmtDay(arrive)}`;
  }
  if (order.status === "paid") {
    const dispatch = skipWeekend(new Date(created.getTime() + 24 * 3600 * 1000));
    const arrive = skipWeekend(new Date(dispatch.getTime() + 24 * 3600 * 1000));
    return `Dispatch ${fmtDay(dispatch)} · Arrives ${fmtDay(arrive)}`;
  }
  return null;
}

/* --------- Page --------- */
export default function Track() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const cart = useCart();
  const nav = useNavigate();
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [pollTick, setPollTick] = useState(0);

  usePageMeta({
    title: "Track your order · Vape Essence",
    description:
      "Look up any Vape Essence order using your order ID and email. Real-time status: placed → paid → dispatched → delivered.",
  });

  const lookup = useCallback(
    async (opts = {}) => {
      const useOrderId = opts.orderId ?? orderId;
      const useEmail = opts.email ?? email;
      if (!useOrderId || !useEmail) return;
      if (!opts.silent) setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get(`/orders/track/${useOrderId.trim()}`, {
          params: { email: useEmail.trim() },
        });
        setOrder(data);
        setParams({ order_id: useOrderId.trim(), email: useEmail.trim() });
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        else if (!opts.silent) toast.error("Something went wrong. Please try again.");
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [orderId, email, setParams]
  );

  // Auto-fetch from URL params on first render
  useEffect(() => {
    if (params.get("order_id") && params.get("email")) {
      lookup({ silent: true, orderId: params.get("order_id"), email: params.get("email") });
    }
    // eslint-disable-next-line
  }, []);

  // Auto-poll every 60s while an in-progress order is loaded
  useEffect(() => {
    if (!order) return;
    if (order.status === "delivered" || order.status === "cancelled" || order.status === "failed") return;
    const id = setInterval(() => setPollTick((n) => n + 1), POLL_MS);
    return () => clearInterval(id);
  }, [order]);
  useEffect(() => {
    if (pollTick > 0) lookup({ silent: true });
    // eslint-disable-next-line
  }, [pollTick]);

  // Signed-in users: fetch their orders for the quick-pick dropdown
  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then((r) => setMyOrders(r.data || [])).catch(() => {});
  }, [user]);

  const failed = order?.status === "cancelled" || order?.status === "failed";
  const stepIndex = order ? STATUS_INDEX[order.status] ?? 0 : 0;
  const eta = useMemo(() => etaForOrder(order), [order]);

  const copyOrderId = () => {
    navigator.clipboard.writeText(order.order_id);
    toast.success("Order ID copied");
  };

  const reorder = () => {
    if (!order?.items?.length) return;
    order.items.forEach((it) => {
      cart.add({
        product_slug: it.product_slug || it.slug,
        name: it.name,
        unit_price: it.unit_price,
        quantity: it.quantity,
        variant: it.variant || null,
        image: it.image || null,
      });
    });
    toast.success("Added to cart", { description: `${order.items.length} item(s) from order ${order.order_id}` });
    nav("/cart");
  };

  return (
    <div className="mx-auto max-w-[900px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
      <div className="font-label text-xs text-[#00FFC2]">/ ORDER TRACKING</div>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
        Where's <span className="text-[#FF5A1F]">my order</span>?
      </h1>
      <p className="mt-3 sm:mt-4 max-w-lg text-sm sm:text-base text-white/60">
        Look up any order using your order ID and the email address used at checkout.
      </p>

      {/* Signed-in quick pick */}
      {user && myOrders.length > 0 && (
        <div
          data-testid="track-my-orders"
          className="mt-8 border border-[#00FFC2]/30 bg-[#0d0d0d] p-4 sm:p-5"
        >
          <div className="flex items-center gap-2 mb-3">
            <CaretDown size={13} weight="bold" className="text-[#00FFC2]" />
            <div className="font-label text-[10px] tracking-widest text-[#00FFC2]">
              YOUR RECENT ORDERS
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {myOrders.slice(0, 6).map((o) => (
              <button
                key={o.order_id}
                type="button"
                data-testid={`track-my-order-${o.order_id}`}
                onClick={() => {
                  setOrderId(o.order_id);
                  setEmail(o.customer_email || user.email);
                  lookup({ orderId: o.order_id, email: o.customer_email || user.email });
                }}
                className="flex items-center justify-between gap-3 border border-white/10 bg-black px-3 py-2 text-left hover:border-[#FF5A1F] transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-mono text-[11px] truncate">{o.order_id}</div>
                  <div className="mt-0.5 text-[10px] text-white/50">
                    {new Date(o.created_at).toLocaleDateString()} · {o.status}
                  </div>
                </div>
                <div className="font-display text-sm font-bold text-[#00FFC2] shrink-0">
                  {ZAR(o.amount)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lookup form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          lookup();
        }}
        data-testid="track-form"
        className="mt-6 sm:mt-8 grid gap-3 border border-white/10 bg-[#0d0d0d] p-4 sm:p-6 md:grid-cols-[1.2fr_1.5fr_auto]"
      >
        <input
          data-testid="track-order-id"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="Order ID (VE-XXXX…)"
          required
          className="border border-white/15 bg-black px-4 py-3 text-sm uppercase outline-none focus:border-[#FF5A1F]"
        />
        <input
          data-testid="track-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email used at checkout"
          required
          className="border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
        />
        <button
          data-testid="track-submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 bg-[#00FFC2] px-5 py-3 font-label text-xs text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
        >
          <MagnifyingGlass size={14} weight="bold" />
          {loading ? "Looking..." : "Track"}
        </button>
      </form>

      {notFound && (
        <div data-testid="track-not-found" className="mt-8 border border-[#FF5A1F]/50 bg-[#FF5A1F]/5 p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <XCircle size={22} weight="duotone" className="text-[#FF5A1F]" />
            <div className="font-display text-lg font-bold">No matching order</div>
          </div>
          <p className="mt-2 text-sm text-white/60">
            Double-check the order ID and email address — they must match exactly.
          </p>
        </div>
      )}

      {order && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="track-result"
          className="mt-8 sm:mt-10 space-y-6 sm:space-y-8"
        >
          {/* Header */}
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 pb-5 sm:pb-6">
            <div>
              <div className="font-label text-[10px] text-white/40">ORDER</div>
              <div className="mt-1 flex items-center gap-2">
                <span data-testid="track-order-code" className="font-display text-xl sm:text-2xl font-black">
                  {order.order_id}
                </span>
                <button
                  data-testid="track-copy-id"
                  onClick={copyOrderId}
                  aria-label="Copy order ID"
                  className="inline-flex h-7 w-7 items-center justify-center border border-white/15 hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
                >
                  <Copy size={12} weight="bold" />
                </button>
              </div>
              <div className="mt-1 text-[11px] sm:text-xs text-white/50">
                Placed {new Date(order.created_at).toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="font-label text-[10px] text-white/40">TOTAL</div>
              <div className="mt-1 font-display text-xl sm:text-2xl font-black text-[#00FFC2]">
                {ZAR(order.amount)}
              </div>
              {eta && (
                <div className="mt-1 text-[11px] font-label text-[#FFC900]">{eta}</div>
              )}
            </div>
          </div>

          {/* Status pipeline */}
          {failed ? (
            <FailedCard order={order} />
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="font-label text-[10px] text-white/40">STATUS</div>
                <div className="inline-flex items-center gap-1 text-[10px] font-label text-white/40">
                  <ArrowClockwise size={11} weight="bold" className="animate-spin-slow" />
                  Auto-refreshing
                </div>
              </div>
              <div className="relative grid grid-cols-4 gap-1 sm:gap-2">
                <div className="absolute left-4 right-4 top-4 h-[2px] bg-white/10">
                  <div
                    className="h-full bg-gradient-to-r from-[#FF5A1F] to-[#00FFC2] transition-all duration-700"
                    style={{ width: `${(stepIndex / 3) * 100}%` }}
                  />
                </div>
                {STATUS_STEPS.map((s, i) => {
                  const done = i <= stepIndex;
                  const current = i === stepIndex;
                  return (
                    <div key={s.key} data-testid={`track-step-${s.key}`} className="relative flex flex-col items-center text-center">
                      <div
                        className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border ${
                          done
                            ? "border-[#00FFC2] bg-[#00FFC2] text-black"
                            : "border-white/20 bg-black text-white/40"
                        } ${current ? "ring-4 ring-[#00FFC2]/20 animate-pulse" : ""}`}
                      >
                        <s.Icon size={14} weight="bold" />
                      </div>
                      <div className={`mt-3 font-label text-[9px] sm:text-[10px] leading-tight ${done ? "text-white" : "text-white/40"}`}>
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tracking card — only when shipped and info present */}
              {order.status === "shipped" && (order.tracking_number || order.tracking_url) && (
                <TrackingCard order={order} />
              )}

              {order.status === "shipped" && !order.tracking_number && !order.tracking_url && (
                <div className="mt-6 border border-[#FFC900]/40 bg-[#FFC900]/5 p-4 text-sm text-white/70">
                  <b className="text-[#FFC900]">In transit.</b> Your parcel is on its way — expect delivery within 24-48 hrs nationwide.
                </div>
              )}
              {order.status === "paid" && (
                <div className="mt-6 border border-[#00FFC2]/40 bg-[#00FFC2]/5 p-4 text-sm text-white/70">
                  <b className="text-[#00FFC2]">Payment received.</b> We're packing your order and will dispatch within the next business day.
                </div>
              )}
              {(order.status === "pending" || order.status === "initiated") && (
                <div className="mt-6 border border-white/15 bg-white/5 p-4 text-sm text-white/70">
                  Awaiting payment confirmation from PayFast. This usually clears in a minute.
                </div>
              )}
              {order.status === "delivered" && (
                <div className="mt-6 border border-[#00FFC2]/40 bg-[#00FFC2]/5 p-4 text-sm text-white/70">
                  <b className="text-[#00FFC2]">Delivered.</b> Thanks for shopping with Vape Essence.
                  Enjoyed it? <Link to={`/product/${order.items?.[0]?.product_slug || ""}#reviews`} className="underline hover:text-white">Leave a review</Link>.
                </div>
              )}
            </div>
          )}

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="font-label text-[10px] text-white/40">ITEMS</div>
              <button
                data-testid="track-reorder"
                onClick={reorder}
                className="inline-flex items-center gap-1.5 border border-[#FF5A1F] bg-[#FF5A1F]/10 px-3 py-1.5 font-label text-[10px] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-black transition-colors"
              >
                <Repeat size={11} weight="bold" /> BUY IT AGAIN
              </button>
            </div>
            <div className="border border-white/10">
              {order.items?.map((it, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 sm:gap-4 border-b border-white/10 p-3 sm:p-4 last:border-b-0"
                >
                  <div className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 overflow-hidden border border-white/10 bg-black">
                    {it.image ? (
                      <img src={mediaUrl(it.image)} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-bold truncate">{it.name}</div>
                    {it.variant && (
                      <div className="mt-1 text-[11px] text-white/50 line-clamp-2">
                        {Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-white/60 shrink-0">×{it.quantity}</div>
                  <div className="w-16 sm:w-20 text-right text-xs sm:text-sm text-[#00FFC2] shrink-0">
                    {ZAR(it.unit_price * it.quantity)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping + totals */}
          <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
            <div className="border border-white/10 bg-[#0d0d0d] p-4 sm:p-6">
              <div className="font-label text-[10px] text-white/40">SHIP TO</div>
              <div className="mt-3 text-sm text-white/80">
                {order.customer_first_name} {order.customer_last_name}
              </div>
              <div className="mt-1 text-sm text-white/60 whitespace-pre-line">
                {order.shipping_address}
              </div>
              {order.customer_phone && (
                <div className="mt-1 text-sm text-white/60">{order.customer_phone}</div>
              )}
            </div>
            <div className="border border-white/10 bg-[#0d0d0d] p-4 sm:p-6 space-y-2 text-sm">
              <div className="flex justify-between text-white/60">
                <span>Subtotal</span>
                <span>{ZAR(order.subtotal)}</span>
              </div>
              {order.discount_value > 0 && (
                <div className="flex justify-between text-[#00FFC2]">
                  <span>Discount</span>
                  <span>-{ZAR(order.discount_value)}</span>
                </div>
              )}
              {order.points_redeem_value > 0 && (
                <div className="flex justify-between text-[#FFC900]">
                  <span>Points redeemed</span>
                  <span>-{ZAR(order.points_redeem_value)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-white/10 pt-3 text-white">
                <span className="font-label text-xs">TOTAL</span>
                <span className="font-display font-black text-[#00FFC2]">{ZAR(order.amount)}</span>
              </div>
              {order.points_earned > 0 && (
                <div className="pt-2 text-xs text-[#FFC900]">
                  +{order.points_earned} loyalty points{" "}
                  {["paid", "shipped", "delivered"].includes(order.status) ? "awarded" : "pending"}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

/* --------- Sub-components --------- */

function TrackingCard({ order }) {
  const copy = () => {
    navigator.clipboard.writeText(order.tracking_number || order.tracking_url || "");
    toast.success("Tracking copied");
  };
  return (
    <div
      data-testid="track-tracking-card"
      className="mt-6 border border-[#FFC900]/50 bg-gradient-to-br from-[#1a1305] via-[#0d0d0d] to-[#0a0d0c] p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Truck size={14} weight="fill" className="text-[#FFC900]" />
        <span className="font-label text-[10px] tracking-widest text-[#FFC900]">
          {order.courier ? `${order.courier.toUpperCase()} · TRACKING` : "TRACKING"}
        </span>
      </div>
      {order.tracking_number && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="font-label text-[10px] text-white/40">TRACKING NUMBER</div>
            <div className="mt-1 font-mono text-base sm:text-lg text-white break-all">
              {order.tracking_number}
            </div>
          </div>
          <button
            onClick={copy}
            data-testid="track-copy-tracking"
            className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 font-label text-[10px] hover:border-[#FFC900] hover:text-[#FFC900] transition-colors"
          >
            <Copy size={11} weight="bold" /> COPY
          </button>
        </div>
      )}
      {order.tracking_url && (
        <a
          href={order.tracking_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="track-tracking-link"
          className="inline-flex items-center gap-1.5 bg-[#FFC900] px-4 py-2 font-label text-[11px] text-black hover:-translate-y-0.5 transition-transform"
        >
          TRACK PARCEL LIVE <ArrowRight size={11} weight="bold" />
        </a>
      )}
    </div>
  );
}

function FailedCard({ order }) {
  return (
    <div className="border border-[#FF5A1F]/40 bg-[#FF5A1F]/5 p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <XCircle size={22} weight="duotone" className="text-[#FF5A1F]" />
        <div className="font-display text-lg font-bold">
          Order {order.status === "cancelled" ? "cancelled" : "payment failed"}
        </div>
      </div>
      <p className="mt-2 text-sm text-white/60">
        {order.status === "cancelled"
          ? "This order was cancelled before payment. You can start a fresh order any time."
          : "Payment couldn't be completed. Feel free to try again — your cart is safe."}
      </p>
      <Link
        to="/shop"
        className="mt-6 inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-label text-xs hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
      >
        Back to shop <ArrowRight size={12} weight="bold" />
      </Link>
    </div>
  );
}
